import { stripLocalePrefix } from './public-audit-path';

/**
 * cas-ee99 (Ben, #gabber-internal 2026-10-03): a small header stripe telling
 * existing creators about the October 2026 version. It is dismissable, shown on
 * the first signed-in visit after rollout, and only to accounts that existed
 * before the rollout: a brand-new signup has no "old version" to compare with.
 *
 * Bump the id for a future announcement; the cutoff sits next to it.
 */
export const BETA_UPDATE_ANNOUNCEMENT_ID = 'beta-update-2026-10';

/** Accounts created at or after this instant never see the announcement. */
export const BETA_UPDATE_ACCOUNT_CUTOFF = '2026-10-04T00:00:00.000Z';

const STORAGE_PREFIX = `gabber:announcement:${BETA_UPDATE_ANNOUNCEMENT_ID}`;

/** localStorage: the account closed it. Never shown again on this device. */
export function betaUpdateDismissedKey(accountUid: string): string {
  return `${STORAGE_PREFIX}:dismissed:${accountUid}`;
}

/** localStorage: the account has had its first visit with the announcement. */
export function betaUpdateSeenKey(accountUid: string): string {
  return `${STORAGE_PREFIX}:seen:${accountUid}`;
}

/** sessionStorage: this browser session is that first visit, so keep showing it here. */
export function betaUpdateVisitKey(accountUid: string): string {
  return `${STORAGE_PREFIX}:visit:${accountUid}`;
}

/** True only for a parseable signup time strictly before the cutoff. Unknown means no. */
export function isBetaUpdateAccountEligible(createdAt: string | null | undefined): boolean {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;
  return created < Date.parse(BETA_UPDATE_ACCOUNT_CUTOFF);
}

const HIDDEN_PREFIXES = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/create-account',
  '/create-workspace',
  '/begin',
  '/start',
  '/setup',
  '/pricing',
  '/checkout',
  '/admin',
  '/terms',
  '/privacy'
];

function pathnameOf(path: string): string {
  const cut = path.search(/[?#]/);
  return stripLocalePrefix(cut === -1 ? path : path.slice(0, cut));
}

/**
 * Auth, onboarding, checkout, admin, legal and the public audit view are
 * focused flows; the stripe waits for the creator's own app pages.
 */
export function isBetaUpdateHiddenOnPath(path: string | null | undefined): boolean {
  if (!path) return true;
  const pathname = pathnameOf(path);
  if (/^\/audit\/[^/]+$/.test(pathname)) return true;
  return HIDDEN_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(kind: 'localStorage' | 'sessionStorage'): StorageLike | null {
  try {
    const store = globalThis[kind];
    return store ?? null;
  } catch {
    return null;
  }
}

function read(store: StorageLike | null, key: string): string | null {
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(store: StorageLike | null, key: string, value: string): void {
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch {
    // Private browsing and full storage should never block the app.
  }
}

export interface BetaUpdateAudience {
  accountUid: string | null | undefined;
  createdAt: string | null | undefined;
  /** Admin "view as" sessions must not spend the creator's one showing. */
  impersonating?: boolean;
}

/**
 * Decide whether this account should see the announcement now, and if so
 * claim the first visit. Dismissed → never. Seen in an earlier browser
 * session → never. Otherwise it shows for the whole of this session until the
 * creator closes it.
 */
export function claimBetaUpdateVisit(audience: BetaUpdateAudience): boolean {
  const uid = audience.accountUid;
  if (!uid || audience.impersonating) return false;
  if (!isBetaUpdateAccountEligible(audience.createdAt)) return false;

  const local = browserStorage('localStorage');
  const session = browserStorage('sessionStorage');
  if (!local) return false;
  if (read(local, betaUpdateDismissedKey(uid)) === '1') return false;

  const seen = read(local, betaUpdateSeenKey(uid)) === '1';
  const thisVisit = read(session, betaUpdateVisitKey(uid)) === '1';
  if (seen && !thisVisit) return false;

  write(local, betaUpdateSeenKey(uid), '1');
  write(session, betaUpdateVisitKey(uid), '1');
  return true;
}

export function dismissBetaUpdate(accountUid: string | null | undefined): void {
  if (!accountUid) return;
  write(browserStorage('localStorage'), betaUpdateDismissedKey(accountUid), '1');
}
